import { useSelector } from 'react-redux';
import {
  serverTimestamp,
  collection,
  setDoc,
  doc,
  arrayUnion,
  updateDoc,
  getDoc
} from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase';

import { defaultDoc, parseFirestorePath } from '../../utils/Schema';
//import useAutomation, { AUTOMATION_ACTION_TYPE } from '../automation';
import useDatabase from '../database';
import { parseFieldMarkup } from '../../utils/TextProcessing';

/* creates a new document based on the supplied route and returns the new document's id */
const useCreate = ignoreAutomations => {
  const { getDb } = useDatabase();
  //const { onDocumentAction } = useAutomation(ignoreAutomations);
  const { types } = useSelector(state => state.Content);
  const { parentRoutes } = useSelector(state => state.Routing);
  const userData = useSelector(state => state.User);
  const [authUser, authUserLoading] = useAuthState(auth);

  const { documents } = useSelector(state => state.Documents);
  /* 
    creates a brand new document based on the supplied route

    can supply either: contentTypeName and path or route and params

  */
  const createDocument = async ({
    data,
    contentTypeName,
    contentType,
    parentKeyData,
    path,
    route,
    params,
    sortOrder = 1
  }) => {
    let targetContentType = contentType
      ? contentType
      : types.find(t => t.type === contentTypeName || t.type === route.type);

    const { db, parentType } = await getDb({
      relationship: route?.relationship,
      contentTypeName: targetContentType?.type
    });

    if (!targetContentType && contentTypeName) {
      const docSnap = await getDoc(
        doc(db, 'types', contentTypeName),
        contentTypeName
      );

      if (docSnap.exists()) {
        targetContentType = docSnap.data();
      }
    }

    let additionalUserData = {};
    if (userData) {
      additionalUserData = {
        ...userData.session,
        ...userData
      };
    }
    let defDoc = {};

    if (targetContentType) {
      defDoc = defaultDoc(targetContentType, 0, undefined, additionalUserData);
    }

    /* This applies in the back office only, since the mobile app handles assigning default values differenlty based on data sources
      Sometimes we want to set a document default value to a value of a parent document one or more levels up
      This can be done by using a default value of the form
      params.user_programId.name <==== this will look at the parent routes, extract any route with idField = user_programId, 
        then get the document from the correct types collection in redux
      or params.user_programId <===== this will just set the field value to the value or params.user_programId
    */
    targetContentType?.fields
      .filter(
        f => f.defaultValue?.indexOf && f.defaultValue?.indexOf('param.') > -1
      )
      .forEach(field => {
        const parts = field.defaultValue.split('.');
        if (parts.length === 2) {
          defDoc[field.name] =
            params[parts[1]] === undefined ? '' : params[parts[1]];
          delete data[field.name];
        } else {
          const parentRouteType = parentRoutes.find(p => p.idField === parts[1])
            ?.type;
          if (parentRouteType) {
            const group = documents?.find(d => d.type === parentRouteType);
            if (group) {
              const doc = group.documents.find(d => d.id === params[parts[1]]);
              if (doc && doc[parts[2]] !== undefined) {
                defDoc[field.name] = doc[parts[2]];
              }
            }
          }

          if (
            defDoc[field.name]?.indexOf &&
            defDoc[field.name].indexOf('param.') > -1
          ) {
            defDoc[field.name] = '';
          }
        }
      });

    defDoc = {
      ...parentKeyData,
      ...defDoc,
      sortOrder,
      ...data,
      ...params,
      lastUpdatedBy: userData?.session?.email || 'System Login',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    let documentPath =
      path ||
      parseFirestorePath({
        route,
        params,
        contentType: targetContentType,
        parentType,
        organisationId: userData.selectedOrganisationId
      });

    const applicableParentRoutes = targetContentType
      ? parentRoutes.filter(p => p.type !== targetContentType.type)
      : [];

    let relationship;

    if (
      !parentKeyData &&
      targetContentType &&
      applicableParentRoutes.length > 0 &&
      params
      // documentPath.indexOf(targetContentType.collectionName) !== 0 // because otherwise we cannot set parent keys on documents created in types that have their own collections
    ) {
      const parentType = types.find(
        t =>
          t.type ===
          applicableParentRoutes[applicableParentRoutes.length - 1].type
      );
      relationship = parentType.relationships.find(
        r =>
          r.contentTypeName === route?.type ||
          r.contentTypeName === contentType?.type
      );

      const paramNames = Object.keys(params);

      if (!params[relationship.childKey]) {
        defDoc[relationship.childKey] =
          params[paramNames[paramNames.length - 1]];
      } else {
        defDoc[relationship.childKey] = params[relationship.childKey];
      }

      // Adding a related item to an existing record but the related item has its own collection
      if (targetContentType.hasOwnCollection) {
        documentPath = targetContentType.collectionName;
      }

      // Apply any custom field mappings defined on this relationship
      if (relationship.useCustomFieldMappings) {
        try {
          const mappings = JSON.parse(relationship.customFieldMappings);
          mappings.forEach(mapping => {
            Object.keys(params).forEach(paramName => {
              mapping.value = mapping.value.replace(
                `params.${paramName}`,
                params[paramName]
              );
            });
            defDoc[mapping.field] = mapping.value;
          });
        } catch {
          // na
        }
      }
    }

    if (userData?.selectedOrganisationId && !defDoc.organisationId) {
      defDoc.organisationId = userData.selectedOrganisationId;
    }

    if (contentTypeName === 'user' || targetContentType.type === 'user') {
      try {
        console.log({ authUser, authUserLoading });
        defDoc.id = defDoc.userID;
        await setDoc(doc(db, 'users', defDoc.userID), defDoc);
        return { document: defDoc, id: defDoc.id };
      } catch (error) {
        console.error('Error creating user document:', error);
        return;
      }
    } else {
      const docRef = doc(collection(db, documentPath));
      // update the id
      let docData = {
        ...defDoc,
        id: targetContentType.type === 'user' ? defDoc.userID : docRef.id
      };

      let parentDocumentId;
      let parentPath;
      let parentDocRef;
      let parentDoc = {};

      if (relationship?.childKey) {
        parentDocumentId = docData[relationship.childKey];
        parentPath = documentPath
          .replace(`/${relationship.contentTypeName}`, '')
          .replace(`/${parentDocumentId}`);

        const segments = documentPath.split('/');
        segments.splice(segments.length - 2, 2);
        parentPath = segments.join('/');

        if (parentPath?.trim().length > 0) {
          parentDocRef = await getDoc(doc(db, parentPath, parentDocumentId));
        }
      }

      if (relationship?.useCustomFieldMappings && parentDocRef?.exists()) {
        try {
          parentDoc = parentDocRef.data();
          const mappings = JSON.parse(relationship.customFieldMappings);
          mappings.forEach(mapping => {
            Object.keys(params).forEach(paramName => {
              mapping.value = mapping.value.replace(
                `params.${paramName}`,
                params[paramName]
              );
            });

            docData[mapping.field] = parseFieldMarkup({
              record: parentDoc,
              fieldMarkup: mapping.value,
              returnAsString: true
            });
          });
        } catch {
          // na
        }
      }

      if (relationship?.useNestedArray) {
        //const { db } = await getDb({ relationship });

        const parentData = {};

        // servier time stamps cannot be used with arrayUniion
        Object.keys(docData).forEach(key => {
          if (docData[key]._methodName === 'serverTimestamp') {
            docData[key] = Date.now();
          }
        });

        parentData[
          relationship.nestedArrayKey?.trim().length > 0
            ? relationship.nestedArrayKey
            : targetContentType.collectionName
        ] = arrayUnion(docData);

        if (
          relationship.useCustomFirestorePath &&
          relationship.customFirestorePath?.trim().length > 0
        ) {
          parentPath = documentPath;
        }

        await updateDoc(doc(db, parentPath, parentDocumentId), parentData);
      } else {
        await setDoc(doc(db, documentPath, docRef.id), docData, {
          merge: true
        });
      }

      /*await onDocumentAction({
        action: AUTOMATION_ACTION_TYPE.RECORD_CREATED,
        document: docData,
        id: docData.id,
        contentType: targetContentType,
        path: `${documentPath}/${docData.id}`
      });*/

      return { document: docData, id: docData.id, path: documentPath };
    }
  };

  return {
    createDocument
  };
};

export default useCreate;
